/**
 * Bestellingen Service - API calls voor inkooporders
 */

import apiFetch from './api';

export type BestellingStatus = 'concept' | 'besteld' | 'onderweg' | 'ontvangen' | 'geannuleerd';

export interface BestellingRegel {
  id?: number;
  bestelling_id?: number;
  voorraad_item_id?: number | null;
  klant_id?: number | null;
  omschrijving: string;
  artikelnummer?: string | null;
  aantal_besteld: number;
  aantal_ontvangen?: number;
  stukprijs?: number;
  korting_percentage?: number;
  btw_percentage?: number;
  regel_totaal?: number;
  created_at?: string;
}

export interface Bestelling {
  id: number;
  bestelnummer?: string | null;
  leverancier_id?: number | null;
  leverancier_naam?: string | null;
  status: BestellingStatus;
  besteldatum?: string | null;
  verwachte_leverdatum?: string | null;
  ontvangstdatum?: string | null;
  referentie?: string | null;
  notities?: string | null;
  totaal_excl_btw?: number;
  btw_bedrag?: number;
  totaal_incl_btw?: number;
  created_at: string;
  updated_at?: string | null;
  regels: BestellingRegel[];
}

export interface BestellingListItem {
  id: number;
  bestelnummer?: string | null;
  leverancier_naam?: string | null;
  referentie?: string | null;
  omschrijving?: string | null;
  artikelnummer?: string | null;
  aantal?: number | null;
  stukprijs?: number | null;
  status: BestellingStatus;
  besteldatum?: string | null;
  verwachte_leverdatum?: string | null;
  totaal_incl_btw?: number;
  aantal_regels: number;
  created_at: string;
}

export interface BestellingCreate {
  leverancier_id?: number | null;
  leverancier_naam?: string | null;
  status?: BestellingStatus;
  besteldatum?: string | null;
  verwachte_leverdatum?: string | null;
  referentie?: string | null;
  notities?: string | null;
  regels?: Omit<BestellingRegel, 'id' | 'bestelling_id' | 'created_at'>[];
}

// Type voor het aanmaken/bewerken in het formulier (leverancier_id is belangrijk!)
export interface BestellingFormData {
  leverancier_id?: number;
  leverancier_naam?: string;
  status?: BestellingStatus;
  verwachte_leverdatum?: string;
  referentie?: string;
  notities?: string;
  regels: Omit<BestellingRegel, 'id' | 'bestelling_id' | 'created_at'>[];
}

export interface BestellingUpdate {
  leverancier_id?: number | null;
  leverancier_naam?: string | null;
  status?: BestellingStatus;
  besteldatum?: string | null;
  verwachte_leverdatum?: string | null;
  ontvangstdatum?: string | null;
  referentie?: string | null;
  notities?: string | null;
}

export const bestellingenService = {
  async getAll(status?: BestellingStatus): Promise<BestellingListItem[]> {
    const params = status ? `?status=${status}` : '';
    return apiFetch(`/api/v1/bestellingen${params}`);
  },

  async getById(id: number): Promise<Bestelling> {
    return apiFetch(`/api/v1/bestellingen/${id}`);
  },

  async create(data: BestellingCreate): Promise<Bestelling> {
    return apiFetch('/api/v1/bestellingen', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async update(id: number, data: BestellingUpdate): Promise<Bestelling> {
    return apiFetch(`/api/v1/bestellingen/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async delete(id: number): Promise<void> {
    return apiFetch(`/api/v1/bestellingen/${id}`, {
      method: 'DELETE'
    });
  },

  // Regel operations
  async addRegel(bestellingId: number, regel: Omit<BestellingRegel, 'id' | 'bestelling_id' | 'created_at'>): Promise<BestellingRegel> {
    return apiFetch(`/api/v1/bestellingen/${bestellingId}/regels`, {
      method: 'POST',
      body: JSON.stringify(regel)
    });
  },

  async updateRegel(bestellingId: number, regelId: number, regel: Partial<BestellingRegel>): Promise<BestellingRegel> {
    return apiFetch(`/api/v1/bestellingen/${bestellingId}/regels/${regelId}`, {
      method: 'PUT',
      body: JSON.stringify(regel)
    });
  },

  async deleteRegel(bestellingId: number, regelId: number): Promise<void> {
    return apiFetch(`/api/v1/bestellingen/${bestellingId}/regels/${regelId}`, {
      method: 'DELETE'
    });
  },

  // Actions
  async markeerAlsBesteld(id: number): Promise<{ message: string; besteldatum: string }> {
    return apiFetch(`/api/v1/bestellingen/${id}/bestellen`, {
      method: 'POST'
    });
  },

  async markeerAlsOntvangen(id: number): Promise<{ message: string }> {
    return apiFetch(`/api/v1/bestellingen/${id}/ontvangen`, {
      method: 'POST'
    });
  },

  async bulkBestellen(ids: number[], referentie?: string): Promise<{ message: string; aantal_leveranciers: number; aantal_bestellingen: number }> {
    return apiFetch('/api/v1/bestellingen/bulk-bestellen', {
      method: 'POST',
      body: JSON.stringify({ ids, referentie })
    });
  }
};
